import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Nav } from 'react-bootstrap';
import Select from 'react-select';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import {
  get_projects,
  get_models,
  get_unique_countries,
  get_count_recipients,
  list_campaigns,
  get_campaign,
  save_campaign,
  delete_campaign,
  dispatch_campaign,
  get_campaign_stats,
  cancel_campaign,
} from '../../utils/services';
import { list_QRs } from '../../utils/crm_services';
import Modal_proyectos from '../menus/Modal';
import './MassMessaging.css';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  ChartDataLabels,
  CategoryScale,
  LinearScale,
  BarElement
);

const SERIAL_SEPARATOR_REGEX = /[\s,]+/;
const RECIPIENT_TAB_KEYS = ['proyecto', 'pais', 'modelo', 'serial'];
const MESSAGE_MAX_LENGTH = 300;

/** URL corta tipo QRshorter: https://tomi.la/{short_word} */
const TOMI_LA_SHORT_BASE = 'https://tomi.la/';

function toTomiLaShortUrl(shortWordOrUrl) {
  const raw = (shortWordOrUrl ?? '').toString().trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  const path = raw.replace(/^tomi\.la\/?/i, '').replace(/^\/+/, '');
  return path ? `${TOMI_LA_SHORT_BASE}${path}` : '';
}

/** Extrae el short word guardado en BD (slug o URL tomi.la). */
function shortSlugFromStoredCallAction(stored) {
  const s = (stored ?? '').toString().trim();
  if (!s) return '';
  const m = s.match(/^https?:\/\/tomi\.la\/(.+)$/i);
  if (m) return m[1].replace(/\/$/, '');
  const m2 = s.match(/^tomi\.la\/(.+)$/i);
  if (m2) return m2[1].replace(/\/$/, '');
  if (!/^https?:\/\//i.test(s) && !s.includes('://') && !s.includes('/')) {
    return s.replace(/^\/+/, '').replace(/\/$/, '');
  }
  return '';
}

function normalizeCallActionForSave(raw) {
  const s = (raw ?? '').toString().trim();
  if (!s) return '';
  const slug = shortSlugFromStoredCallAction(s);
  if (slug) return toTomiLaShortUrl(slug);
  return s;
}

const EMPTY_FORM_SNAPSHOT = {
  campaignName: '',
  message: '',
  callToAction: '',
  recipientTab: 'proyecto',
  dest_project_id: null,
  dest_country: null,
  dest_model: null,
  dest_serials: [],
};

function MassMessaging({ usr_id, usr_name, permission }) {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const serialInputRef = useRef(null);
  const projectsRef = useRef([]);
  const savedFormRef = useRef(null); // último estado guardado/cargado para detectar cambios sin guardar
  const hasUnsavedRef = useRef(false); // para beforeunload (actualizado en efecto)

  // View: 'list' | 'edit' — inicializado desde URL en el efecto de sincronización
  const [viewMode, setViewMode] = useState('list');
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [campaignStatus, setCampaignStatus] = useState(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleDateTime, setScheduleDateTime] = useState('');
  const [dispatchLoading, setDispatchLoading] = useState(false);
  const [confirmSendModalOpen, setConfirmSendModalOpen] = useState(false);
  const [confirmSendMode, setConfirmSendMode] = useState('immediate'); // 'immediate' | 'scheduled'
  const [confirmScheduledDate, setConfirmScheduledDate] = useState(''); // para mostrar en el modal de programar
  const [pendingDispatchId, setPendingDispatchId] = useState(null); // id con el que hacer dispatch al confirmar
  const [pendingDeliveryDate, setPendingDeliveryDate] = useState(null); // null = enviar ahora, string = programado
  const [campaignStats, setCampaignStats] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [sortSerialsBy, setSortSerialsBy] = useState('delivery_date');
  const [sortSerialsOrder, setSortSerialsOrder] = useState('desc');

  // Campaign fields
  const [campaignName, setCampaignName] = useState('');
  const [message, setMessage] = useState('');
  const [callToAction, setCallToAction] = useState('');

  // Recipient tab
  const [recipientTab, setRecipientTab] = useState('proyecto');

  // Por Proyecto
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

  // Por País
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);

  // Por Modelo
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);

  // Por Serial: chips (serials ya confirmados) + texto en curso
  const [serialChips, setSerialChips] = useState([]);
  const [serialInputValue, setSerialInputValue] = useState('');

  // Modal destinatarios
  const [recipientsModalOpen, setRecipientsModalOpen] = useState(false);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientsData, setRecipientsData] = useState(null);
  const [recipientsError, setRecipientsError] = useState(null);

  // Links cortos (QRshorter) para Call2Action
  const [shortLinksOptions, setShortLinksOptions] = useState([]);
  const [shortLinksLoading, setShortLinksLoading] = useState(false);

  // Sincronizar vista e id con la URL (permite refrescar y ver la misma campaña)
  useEffect(() => {
    const idParam = searchParams.get('id');
    const newParam = searchParams.get('new');
    if (idParam) {
      const id = parseInt(idParam, 10);
      if (!Number.isNaN(id)) {
        setViewMode('edit');
        setSelectedCampaignId(id);
        return;
      }
    }
    if (newParam === '1' || newParam === 'true') {
      setViewMode('edit');
      setSelectedCampaignId(null);
      resetForm();
      savedFormRef.current = EMPTY_FORM_SNAPSHOT;
      return;
    }
    setViewMode('list');
    setSelectedCampaignId(null);
    resetForm();
    savedFormRef.current = null;
  }, [searchParams]);

  // Mantener ref actualizado para beforeunload
  useEffect(() => {
    hasUnsavedRef.current = hasUnsavedChanges();
  }, [campaignName, message, callToAction, recipientTab, selectedProject, selectedCountry, selectedModel, serialChips]);

  // Avisar al cerrar/refrescar con cambios sin guardar
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (hasUnsavedRef.current) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // Load campaigns list
  useEffect(() => {
    if (viewMode !== 'list') return;
    async function fetch() {
      setCampaignsLoading(true);
      const list = await list_campaigns(token);
      if (list === 'INVALID') {
        navigate('/');
      } else {
        setCampaigns(Array.isArray(list) ? list : []);
      }
      setCampaignsLoading(false);
    }
    fetch();
  }, [token, navigate, viewMode]);

  projectsRef.current = projects;

  // Refinar selectedProject cuando projects carguen después de la campaña
  useEffect(() => {
    if (viewMode !== 'edit' || projects.length === 0) return;
    setSelectedProject((prev) => {
      if (!prev?.value) return prev;
      const opt = projects.find((p) => String(p.value) === String(prev.value));
      return opt || prev;
    });
  }, [viewMode, projects]);

  // Load campaign when editing
  useEffect(() => {
    if (viewMode !== 'edit' || !selectedCampaignId) return;
    async function fetch() {
      const c = await get_campaign(token, selectedCampaignId);
      if (c === 'INVALID') {
        navigate('/');
      } else if (c && !c.error) {
        setCampaignName(c.name || '');
        setMessage(c.message || '');
        setCallToAction(normalizeCallActionForSave(c.call_action || ''));
        setCampaignStatus(c.status != null ? Number(c.status) : 0);
        const tabIndex = typeof c.destination_type === 'number'
          ? c.destination_type
          : parseInt(c.destination_type, 10);
        const validTab = (!Number.isNaN(tabIndex) && tabIndex >= 0 && tabIndex < RECIPIENT_TAB_KEYS.length)
          ? RECIPIENT_TAB_KEYS[tabIndex]
          : 'proyecto';
        setRecipientTab(validTab);
        const projectOpt = c.dest_project_id
          ? projectsRef.current.find(
              (p) => String(p.value) === String(c.dest_project_id)
            )
          : null;
        setSelectedProject(projectOpt || (c.dest_project_id ? { value: c.dest_project_id, label: String(c.dest_project_id) } : null));
        setSelectedCountry(
          c.dest_country ? { value: c.dest_country, label: c.dest_country } : null
        );
        setSelectedModel(
          c.dest_model ? { value: c.dest_model, label: c.dest_model } : null
        );
        setSerialChips(Array.isArray(c.dest_serials) ? c.dest_serials : []);
        savedFormRef.current = {
          campaignName: c.name || '',
          message: c.message || '',
          callToAction: c.call_action || '',
          recipientTab: validTab,
          dest_project_id: c.dest_project_id ?? null,
          dest_country: c.dest_country ?? null,
          dest_model: c.dest_model ?? null,
          dest_serials: Array.isArray(c.dest_serials) ? [...c.dest_serials] : [],
        };
      } else {
        setCampaignStatus(null);
      }
    }
    fetch();
  }, [token, navigate, viewMode, selectedCampaignId]);

  // Load campaign stats when campaign is sent or cancelled
  useEffect(() => {
    if (viewMode !== 'edit' || !selectedCampaignId || (campaignStatus !== 1 && campaignStatus !== 2)) {
      setCampaignStats(null);
      return;
    }
    async function fetchStats() {
      const res = await get_campaign_stats(token, selectedCampaignId);
      if (res && res.ok) setCampaignStats(res);
    }
    fetchStats();
  }, [token, viewMode, selectedCampaignId, campaignStatus]);

  // Load projects
  useEffect(() => {
    async function fetchProjects() {
      const projectsList = await get_projects(token);
      if (projectsList === 'INVALID') {
        navigate('/');
      } else {
        const projectOptions = projectsList
          .filter((element) => element.t8s > 0)
          .map((element, index) => ({
            id: element.id,
            label: element.project_name + ' ( ' + element.t8s + ' Unds)',
            value: element.project_id,
            index: index,
          }));
        setProjects(projectOptions);
      }
    }
    fetchProjects();
  }, [token, navigate]);

  // Load unique countries from tomi8_db.ip_log
  useEffect(() => {
    async function fetchCountries() {
      const countriesList = await get_unique_countries(token);
      if (countriesList === 'INVALID') {
        navigate('/');
      } else if (Array.isArray(countriesList)) {
        const countryOptions = countriesList.map((item) => ({
          label: item.country,
          value: item.country,
        }));
        setCountries(countryOptions);
      }
    }
    fetchCountries();
  }, [token, navigate]);

  // Load models
  useEffect(() => {
    async function fetchModels() {
      const modelsList = await get_models(token);
      if (modelsList === 'INVALID') {
        navigate('/');
      } else {
        const modelOptions = modelsList.map((element) => ({
          label: element.reference,
          value: element.reference,
        }));
        setModels(modelOptions);
      }
    }
    fetchModels();
  }, [token, navigate]);

  // Load links cortos (QRshorter) para selector Call2Action
  useEffect(() => {
    if (viewMode !== 'edit') return;
    let cancelled = false;
    setShortLinksLoading(true);
    list_QRs(token)
      .then((list) => {
        if (cancelled) return;
        if (list === 'INVALID') return;
        const options = (Array.isArray(list) ? list : []).map((item) => {
          const slug = (item.short_url ?? '').toString().trim();
          const value = slug ? toTomiLaShortUrl(slug) : (item.full_url ?? '').toString().trim();
          const shortDisplay = slug ? `${TOMI_LA_SHORT_BASE.replace(/\/$/, '')}/${slug}` : value;
          const label = item.title
            ? `${item.title} (${shortDisplay})`
            : shortDisplay || value;
          return { value, label, shortSlug: slug };
        }).filter((o) => o.value);
        setShortLinksOptions(options);
      })
      .finally(() => {
        if (!cancelled) setShortLinksLoading(false);
      });
    return () => { cancelled = true; };
  }, [token, viewMode]);

  const resetForm = () => {
    setCampaignName('');
    setMessage('');
    setCallToAction('');
    setRecipientTab('proyecto');
    setSelectedProject(null);
    setSelectedCountry(null);
    setSelectedModel(null);
    setSerialChips([]);
    setSerialInputValue('');
    setCampaignStatus(null);
  };

  const goToList = () => {
    if (hasUnsavedChanges() && !window.confirm('Hay cambios sin guardar. ¿Descartar y volver a la lista?')) {
      return;
    }
    navigate('/MassMessaging');
  };

  const goToEdit = (campaignId = null) => {
    if (hasUnsavedChanges()) {
      const targetId = campaignId != null ? String(campaignId) : 'new';
      const currentId = selectedCampaignId != null ? String(selectedCampaignId) : 'new';
      if (targetId !== currentId && !window.confirm('Hay cambios sin guardar. ¿Descartar y continuar?')) {
        return;
      }
    }
    if (campaignId == null) {
      navigate('/MassMessaging?new=1');
    } else {
      navigate('/MassMessaging?id=' + campaignId);
    }
  };

  const handleSave = async () => {
    if (!campaignName.trim()) {
      alert('Ingrese el nombre de la campaña.');
      return;
    }
    if (message.length > MESSAGE_MAX_LENGTH) {
      alert(`El mensaje no puede superar ${MESSAGE_MAX_LENGTH} caracteres.`);
      return;
    }
    setSaving(true);
    const dest_project_id =
      recipientTab === 'proyecto' ? selectedProject?.value ?? null : null;
    const dest_country =
      recipientTab === 'pais' ? selectedCountry?.value ?? null : null;
    const dest_model =
      recipientTab === 'modelo' ? selectedModel?.value ?? null : null;
    const dest_serials = recipientTab === 'serial' ? serialChips : [];
    const tabIndex = RECIPIENT_TAB_KEYS.indexOf(recipientTab);
    const payload = {
      id: selectedCampaignId || '',
      name: campaignName.trim(),
      destination_type: tabIndex >= 0 ? tabIndex : 0,
      dest_project_id,
      dest_country,
      dest_model,
      dest_serials,
      message,
      call_action: normalizeCallActionForSave(callToAction),
    };
    const res = await save_campaign(token, payload);
    setSaving(false);
    if (res.ok) {
      if (res.id && !selectedCampaignId) {
        setSelectedCampaignId(res.id);
        navigate('/MassMessaging?id=' + res.id);
      }
      savedFormRef.current = {
        campaignName: payload.name,
        message: payload.message,
        callToAction: payload.call_action || '',
        recipientTab: RECIPIENT_TAB_KEYS[payload.destination_type >= 0 ? payload.destination_type : 0],
        dest_project_id: payload.dest_project_id ?? null,
        dest_country: payload.dest_country ?? null,
        dest_model: payload.dest_model ?? null,
        dest_serials: payload.dest_serials ? [...payload.dest_serials] : [],
      };
    } else {
      alert(res.error || 'Error al guardar.');
    }
  };

  const handleDeleteClick = (e, id) => {
    e.stopPropagation();
    setDeleteConfirmId(id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;
    const res = await delete_campaign(token, deleteConfirmId);
    setDeleteConfirmId(null);
    if (res.ok) {
      setCampaigns((prev) => prev.filter((c) => c.id !== deleteConfirmId));
    } else {
      alert('Error al eliminar.');
    }
  };

  const buildSavePayload = () => {
    const dest_project_id =
      recipientTab === 'proyecto' ? selectedProject?.value ?? null : null;
    const dest_country =
      recipientTab === 'pais' ? selectedCountry?.value ?? null : null;
    const dest_model =
      recipientTab === 'modelo' ? selectedModel?.value ?? null : null;
    const dest_serials = recipientTab === 'serial' ? serialChips : [];
    const tabIndex = RECIPIENT_TAB_KEYS.indexOf(recipientTab);
    return {
      id: selectedCampaignId || '',
      name: campaignName.trim(),
      destination_type: tabIndex >= 0 ? tabIndex : 0,
      dest_project_id,
      dest_country,
      dest_model,
      dest_serials,
      message,
      call_action: normalizeCallActionForSave(callToAction),
    };
  };

  const ensureCampaignId = async () => {
    if (selectedCampaignId) return selectedCampaignId;
    if (!campaignName.trim()) {
      alert('Ingrese el nombre de la campaña.');
      return null;
    }
    if (message.length > MESSAGE_MAX_LENGTH) {
      alert(`El mensaje no puede superar ${MESSAGE_MAX_LENGTH} caracteres.`);
      return null;
    }
    setSaving(true);
    const payload = buildSavePayload();
    const res = await save_campaign(token, payload);
    setSaving(false);
    if (!res.ok) {
      alert(res.error || 'Error al guardar.');
      return null;
    }
    const id = res.id;
    if (id) setSelectedCampaignId(id);
    return id || null;
  };

  /** Guarda la campaña con el estado actual del formulario (para persistir cambios) y devuelve el id. */
  const saveCampaignAndGetId = async () => {
    if (!campaignName.trim()) {
      alert('Ingrese el nombre de la campaña.');
      return null;
    }
    if (message.length > MESSAGE_MAX_LENGTH) {
      alert(`El mensaje no puede superar ${MESSAGE_MAX_LENGTH} caracteres.`);
      return null;
    }
    setSaving(true);
    const payload = buildSavePayload();
    const res = await save_campaign(token, payload);
    setSaving(false);
    if (!res.ok) {
      alert(res.error || 'Error al guardar.');
      return null;
    }
    const id = res.id;
    if (id) setSelectedCampaignId(id);
    savedFormRef.current = {
      campaignName: payload.name,
      message: payload.message,
      callToAction: payload.call_action || '',
      recipientTab: RECIPIENT_TAB_KEYS[payload.destination_type >= 0 ? payload.destination_type : 0],
      dest_project_id: payload.dest_project_id ?? null,
      dest_country: payload.dest_country ?? null,
      dest_model: payload.dest_model ?? null,
      dest_serials: payload.dest_serials ? [...payload.dest_serials] : [],
    };
    return id || null;
  };

  const handleEnviar = async () => {
    if (campaignStatus === 1) return;
    const id = await saveCampaignAndGetId();
    if (!id) return;
    setPendingDispatchId(id);
    setPendingDeliveryDate(null);
    setConfirmSendMode('immediate');
    setConfirmSendModalOpen(true);
  };

  const handleProgramarClick = () => {
    if (campaignStatus === 1) return;
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const defaultDatetime =
      now.getFullYear() +
      '-' +
      pad(now.getMonth() + 1) +
      '-' +
      pad(now.getDate()) +
      'T' +
      pad(now.getHours()) +
      ':' +
      pad(now.getMinutes());
    setScheduleDateTime(defaultDatetime);
    setScheduleModalOpen(true);
  };

  const handleProgramarConfirm = async () => {
    if (!scheduleDateTime.trim()) {
      alert('Seleccione fecha y hora de entrega.');
      return;
    }
    const id = await saveCampaignAndGetId();
    if (!id) return;
    const deliveryDate = scheduleDateTime.replace('T', ' ') + ':00';
    const displayDate = new Date(scheduleDateTime.replace('T', ' ')).toLocaleString('es', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    setScheduleModalOpen(false);
    setPendingDispatchId(id);
    setPendingDeliveryDate(deliveryDate);
    setConfirmScheduledDate(displayDate);
    setConfirmSendMode('scheduled');
    setConfirmSendModalOpen(true);
  };

  const getRecipientSummaryText = () => {
    if (recipientTab === 'proyecto') return selectedProject ? `Por Proyecto: ${selectedProject.label}` : 'Por Proyecto: —';
    if (recipientTab === 'pais') return selectedCountry ? `Por País: ${selectedCountry.label}` : 'Por País: —';
    if (recipientTab === 'modelo') return selectedModel ? `Por Modelo: ${selectedModel.label}` : 'Por Modelo: —';
    if (recipientTab === 'serial') return serialChips.length ? `Por Serial: ${serialChips.length} serial(es)` : 'Por Serial: —';
    return '—';
  };

  const handleCancelCampaignClick = () => setCancelConfirmOpen(true);

  const handleCancelCampaignConfirm = async () => {
    if (!selectedCampaignId) return;
    setCancelling(true);
    const res = await cancel_campaign(token, selectedCampaignId);
    setCancelling(false);
    setCancelConfirmOpen(false);
    if (res.ok) {
      setCampaignStatus(2);
      const resStats = await get_campaign_stats(token, selectedCampaignId);
      if (resStats && resStats.ok) setCampaignStats(resStats);
      alert(`Campaña cancelada. Se eliminaron ${res.deleted ?? 0} notificaciones pendientes.`);
    } else {
      alert(res.error || 'Error al cancelar la campaña.');
    }
  };

  /** Fecha/hora actual del usuario en formato YYYY-MM-DD HH:mm:ss (hora local) para el API. */
  const getLocalNowForDelivery = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return (
      now.getFullYear() +
      '-' +
      pad(now.getMonth() + 1) +
      '-' +
      pad(now.getDate()) +
      ' ' +
      pad(now.getHours()) +
      ':' +
      pad(now.getMinutes()) +
      ':' +
      pad(now.getSeconds())
    );
  };

  const handleConfirmSendModalConfirm = async () => {
    if (!pendingDispatchId) return;
    // Envío inmediato: usar hora local del usuario; programado: usar la fecha elegida
    const deliveryDate = pendingDeliveryDate != null && pendingDeliveryDate !== ''
      ? pendingDeliveryDate
      : getLocalNowForDelivery();
    setDispatchLoading(true);
    const res = await dispatch_campaign(token, pendingDispatchId, deliveryDate);
    setDispatchLoading(false);
    setConfirmSendModalOpen(false);
    setPendingDispatchId(null);
    setPendingDeliveryDate(null);
    setConfirmScheduledDate('');
    if (res.ok) {
      setCampaignStatus(1);
      alert(`Campaña ${deliveryDate ? 'programada' : 'enviada'}. Se han creado ${res.inserted ?? res.total_serials ?? 0} notificaciones.`);
    } else {
      alert(res.error || 'Error al enviar la campaña.');
    }
  };

  const formatCreationDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d.replace(' ', 'T'));
    return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString();
  };

  const getCampaignStatusLabel = (status) => {
    const s = status != null ? Number(status) : 0;
    if (s === 1) return 'Enviada';
    if (s === 2) return 'Cancelada';
    return 'Borrador';
  };

  const getNotificationStatusLabel = (status) => {
    const s = status != null ? Number(status) : 0;
    if (s === 1) return 'Entregado';
    if (s === 2) return 'Abierto';
    return 'Pendiente';
  };

  const handleSortSerials = (field) => {
    if (sortSerialsBy === field) {
      setSortSerialsOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortSerialsBy(field);
      setSortSerialsOrder(field === 'delivery_date' ? 'desc' : 'asc');
    }
  };

  const sortedSerials = React.useMemo(() => {
    const list = campaignStats?.serials ?? [];
    if (!list.length) return list;
    const by = sortSerialsBy;
    const order = sortSerialsOrder;
    return [...list].sort((a, b) => {
      let va = a[by];
      let vb = b[by];
      if (by === 'delivery_date') {
        va = va ? new Date(va.replace(' ', 'T')).getTime() : 0;
        vb = vb ? new Date(vb.replace(' ', 'T')).getTime() : 0;
      }
      if (by === 'status') {
        va = Number(va);
        vb = Number(vb);
      }
      if (va < vb) return order === 'asc' ? -1 : 1;
      if (va > vb) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [campaignStats?.serials, sortSerialsBy, sortSerialsOrder]);

  const getFormData = () => ({
    campaignName,
    recipientTab,
    selectedProject,
    selectedCountry,
    selectedModel,
    serialChips,
    message,
    callToAction,
  });

  /** Objeto serializable para comparar si hay cambios sin guardar. */
  const getFormDataForCompare = () => ({
    campaignName: campaignName || '',
    message: message || '',
    callToAction: callToAction || '',
    recipientTab,
    dest_project_id: recipientTab === 'proyecto' ? selectedProject?.value ?? null : null,
    dest_country: recipientTab === 'pais' ? selectedCountry?.value ?? null : null,
    dest_model: recipientTab === 'modelo' ? selectedModel?.value ?? null : null,
    dest_serials: recipientTab === 'serial' ? [...serialChips] : [],
  });

  const hasUnsavedChanges = () => {
    const saved = savedFormRef.current;
    if (saved == null) return false;
    const current = getFormDataForCompare();
    return JSON.stringify(current) !== JSON.stringify(saved);
  };

  // Parsea el texto de seriales: separadores espacio o coma; añade tokens completos como chips
  const handleSerialInputChange = (e) => {
    const value = e.target.value;
    const parts = value.split(SERIAL_SEPARATOR_REGEX).map((s) => s.trim()).filter(Boolean);
    const endsWithSeparator = /[\s,]$/.test(value);

    if (endsWithSeparator) {
      const toAdd = parts.filter((s) => !serialChips.includes(s));
      setSerialChips((prev) => [...prev, ...toAdd]);
      setSerialInputValue('');
    } else if (parts.length > 1) {
      const toAdd = parts.slice(0, -1).filter((s) => !serialChips.includes(s));
      setSerialChips((prev) => [...prev, ...toAdd]);
      setSerialInputValue(parts[parts.length - 1]);
    } else {
      setSerialInputValue(value);
    }
  };

  const removeSerialChip = (index) => {
    setSerialChips((prev) => prev.filter((_, i) => i !== index));
  };

  const clearAllSerials = () => {
    setSerialChips([]);
    setSerialInputValue('');
    serialInputRef.current?.focus();
  };

  const handleSerialInputKeyDown = (e) => {
    if (e.key === 'Backspace' && serialInputValue === '' && serialChips.length > 0) {
      setSerialChips((prev) => prev.slice(0, -1));
    }
  };

  const handleOpenRecipientsModal = async () => {
    let project_id = null;
    let country = null;
    let model = null;
    let serials = null;
    if (recipientTab === 'proyecto') {
      if (!selectedProject?.value) {
        setRecipientsError('Seleccione un proyecto.');
        setRecipientsModalOpen(true);
        setRecipientsData(null);
        return;
      }
      project_id = selectedProject.value;
    } else if (recipientTab === 'pais') {
      if (!selectedCountry?.value) {
        setRecipientsError('Seleccione un país.');
        setRecipientsModalOpen(true);
        setRecipientsData(null);
        return;
      }
      country = selectedCountry.value;
    } else if (recipientTab === 'modelo') {
      if (!selectedModel?.value) {
        setRecipientsError('Seleccione un modelo.');
        setRecipientsModalOpen(true);
        setRecipientsData(null);
        return;
      }
      model = selectedModel.value;
    } else {
      if (!serialChips.length) {
        setRecipientsError('Ingrese al menos un serial.');
        setRecipientsModalOpen(true);
        setRecipientsData(null);
        return;
      }
      serials = serialChips;
    }

    setRecipientsError(null);
    setRecipientsLoading(true);
    setRecipientsModalOpen(true);
    setRecipientsData(null);

    const result = await get_count_recipients(token, {
      recipient_type: recipientTab,
      project_id,
      country,
      model,
      serials,
    });

    setRecipientsLoading(false);
    if (result.error && result.error !== 'INVALID') {
      setRecipientsError(result.error || 'Error al obtener destinatarios');
    } else if (result.error === 'INVALID') {
      navigate('/');
    } else {
      setRecipientsData({
        total_serials: result.total_serials ?? 0,
        total_with_version_gt_214: result.total_with_version_gt_214 ?? 0,
      });
    }
  };

  const totalWithOldVersion = recipientsData
    ? recipientsData.total_serials - recipientsData.total_with_version_gt_214
    : 0;
  const recipientsPieData = recipientsData
    ? {
        labels: [
          'Recibirán ahora (versión > 214)',
          'Recibirán al actualizar el software',
        ],
        datasets: [
          {
            data: [recipientsData.total_with_version_gt_214, totalWithOldVersion],
            backgroundColor: ['#28a745', '#ffc107'],
            borderColor: ['#1e7e34', '#d39e00'],
            borderWidth: 1,
          },
        ],
      }
    : null;

  // Lista de campañas
  if (viewMode === 'list') {
    return (
      <div className="mass-messaging-container mass-messaging-list">
        <h2 className="mass-messaging-title">Mensajería Masiva</h2>
        <div className="mass-messaging-table-wrap">
          {campaignsLoading ? (
            <div className="mass-messaging-loading">
              <span className="spinner-border spinner-border-sm me-2" />
              Cargando campañas...
            </div>
          ) : (
            <table className="table table-hover mass-messaging-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Fecha de creación</th>
                  <th>Creado por</th>
                  <th>Estado</th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr
                    key={c.id}
                    className="mass-messaging-row-clickable"
                    onClick={() => goToEdit(c.id)}
                  >
                    <td>{c.name || '—'}</td>
                    <td>{formatCreationDate(c.creation_date)}</td>
                    <td>{c.created_by_name || '—'}</td>
                    <td>{getCampaignStatusLabel(c.status)}</td>
                    <td>
                      {Number(c.status) === 0 && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-danger p-0"
                          onClick={(e) => handleDeleteClick(e, c.id)}
                          aria-label="Eliminar"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <button
          type="button"
          className="mass-messaging-fab"
          onClick={() => goToEdit(null)}
          aria-label="Nueva campaña"
        >
          <Plus size={28} />
        </button>
        <Modal_proyectos
          title="Confirmar eliminación"
          isOpen={!!deleteConfirmId}
          onClose={() => setDeleteConfirmId(null)}
        >
          <p>¿Está seguro de que desea eliminar esta campaña?</p>
          <div className="modal-footer border-top pt-3 mt-3">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setDeleteConfirmId(null)}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDeleteConfirm}
            >
              Eliminar
            </button>
          </div>
        </Modal_proyectos>
      </div>
    );
  }

  return (
    <div className="mass-messaging-container">
      <div className="mass-messaging-edit-header">
        <button
          type="button"
          className="btn btn-link mass-messaging-back"
          onClick={goToList}
          aria-label="Volver"
        >
          <ArrowLeft size={24} />
        </button>
        <h2 className="mass-messaging-title">Mensajería Masiva</h2>
      </div>

      {(campaignStatus === 1 || campaignStatus === 2) ? (
        <div className="mass-messaging-sent-view">
          {campaignStatus === 2 && (
            <div className="mass-messaging-badge mass-messaging-badge--cancelled">
              Campaña cancelada
            </div>
          )}
          <div className="mass-messaging-compact">
            <p className="mass-messaging-compact-row">
              <span className="mass-messaging-compact-label">Nombre:</span>{' '}
              <span className="mass-messaging-compact-value">{campaignName || '—'}</span>
            </p>
            <p className="mass-messaging-compact-row">
              <span className="mass-messaging-compact-label">Destinatarios:</span>{' '}
              <span className="mass-messaging-compact-value">{getRecipientSummaryText()}</span>
            </p>
            <p className="mass-messaging-compact-row">
              <span className="mass-messaging-compact-label">Mensaje:</span>{' '}
              <span className="mass-messaging-compact-value mass-messaging-compact-message">{message || '—'}</span>
            </p>
            <p className="mass-messaging-compact-row">
              <span className="mass-messaging-compact-label">Call2Action:</span>{' '}
              <span className="mass-messaging-compact-value">{callToAction || '—'}</span>
            </p>
          </div>
          {campaignStatus === 1 && (
            <div className="mass-messaging-actions mass-messaging-actions--sent">
              <button
                type="button"
                className="mass-messaging-btn mass-messaging-btn--cancel"
                onClick={handleCancelCampaignClick}
                disabled={cancelling}
              >
                {cancelling ? 'Cancelando...' : 'Cancelar'}
              </button>
            </div>
          )}
          <div className="mass-messaging-stats">
            <h3 className="mass-messaging-stats-title">Estadísticas de la campaña</h3>
            {campaignStats && campaignStats.ok ? (
              <>
                <p className="mass-messaging-stats-total">
                  Total destinatarios: <strong>{campaignStats.total}</strong>
                </p>
                <p className="mass-messaging-stats-date">
                  Fecha envío: <strong>{campaignStats.creation_date ? new Date(campaignStats.creation_date.replace(' ', 'T')).toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</strong>
                </p>
                <p className="mass-messaging-stats-date">
                  Fecha ejecución: <strong>{campaignStats.execution_date ? new Date(campaignStats.execution_date.replace(' ', 'T')).toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</strong>
                </p>
                {campaignStats.total > 0 && (() => {
                  const s1 = campaignStats.count_status_1 ?? 0;
                  const s2 = campaignStats.count_status_2 ?? 0;
                  const s0Deliverable = campaignStats.count_status_0_deliverable ?? 0;
                  const s0NotDeliverable = campaignStats.count_status_0_not_deliverable ?? 0;
                  const total = campaignStats.total;
                  const barData = {
                    labels: [''],
                    datasets: [
                      { label: 'Entregados', data: [s1], backgroundColor: 'rgba(54, 162, 235, 0.8)' },
                      { label: 'Abiertos', data: [s2], backgroundColor: 'rgba(75, 192, 192, 0.8)' },
                      { label: 'No entregados', data: [s0Deliverable], backgroundColor: 'rgba(255, 206, 86, 0.8)' },
                      { label: 'No entregables', data: [s0NotDeliverable], backgroundColor: 'rgba(255, 99, 132, 0.8)' },
                    ],
                  };
                  const barOptions = {
                    indexAxis: 'y',
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'bottom' },
                      tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw} (${total ? ((ctx.raw / total) * 100).toFixed(1) : 0}%)` } },
                      datalabels: {
                        formatter: (value, ctx) => {
                          const pct = total ? ((value / total) * 100).toFixed(0) : 0;
                          return `${pct}% (${value})`;
                        },
                        color: '#333',
                        font: { weight: 'bold', size: 11 },
                      },
                    },
                    scales: {
                      x: { stacked: true, max: total || 1, ticks: { display: true } },
                      y: { stacked: true, ticks: { display: false } },
                    },
                  };
                  return (
                    <div className="mass-messaging-stats-chart">
                      <Bar data={barData} options={barOptions} />
                    </div>
                  );
                })()}
                {Array.isArray(campaignStats.serials) && campaignStats.serials.length > 0 && (
                  <div className="mass-messaging-stats-serials">
                    <h4 className="mass-messaging-stats-serials-title">Listado por serial</h4>
                    <div className="mass-messaging-stats-table-wrap">
                      <table className="table table-sm table-striped mass-messaging-stats-table">
                        <thead>
                          <tr>
                            <th>
                              <button type="button" className="mass-messaging-sort-btn" onClick={() => handleSortSerials('serial_num')}>
                                Serial {sortSerialsBy === 'serial_num' && (sortSerialsOrder === 'asc' ? ' ↑' : ' ↓')}
                              </button>
                            </th>
                            <th>
                              <button type="button" className="mass-messaging-sort-btn" onClick={() => handleSortSerials('delivery_date')}>
                                Fecha entrega {sortSerialsBy === 'delivery_date' && (sortSerialsOrder === 'asc' ? ' ↑' : ' ↓')}
                              </button>
                            </th>
                            <th>
                              <button type="button" className="mass-messaging-sort-btn" onClick={() => handleSortSerials('status')}>
                                Estado {sortSerialsBy === 'status' && (sortSerialsOrder === 'asc' ? ' ↑' : ' ↓')}
                              </button>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedSerials.map((row) => (
                            <tr key={row.id}>
                              <td>{row.serial_num || '—'}</td>
                              <td>{row.delivery_date ? new Date(row.delivery_date.replace(' ', 'T')).toLocaleString('es', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                              <td>{getNotificationStatusLabel(row.status)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-muted">Cargando estadísticas...</p>
            )}
          </div>
          <Modal_proyectos
            title="Confirmar cancelación"
            isOpen={cancelConfirmOpen}
            onClose={() => !cancelling && setCancelConfirmOpen(false)}
          >
            <p>¿Está seguro de que desea cancelar esta campaña? Se eliminarán de la cola todos los mensajes que aún no se han entregado  y la campaña quedará como cancelada.</p>
            <div className="modal-footer border-top pt-3 mt-3">
              <button type="button" className="btn btn-secondary" onClick={() => setCancelConfirmOpen(false)} disabled={cancelling}>
                No
              </button>
              <button type="button" className="btn btn-danger" onClick={handleCancelCampaignConfirm} disabled={cancelling}>
                {cancelling ? 'Cancelando...' : 'Sí, cancelar campaña'}
              </button>
            </div>
          </Modal_proyectos>
        </div>
      ) : (
      <fieldset className="border-0 p-0 m-0">
      {/* Nombre de la campaña + Creada por */}
      <div className="mass-messaging-row">
        <div className="mass-messaging-field mass-messaging-field--campaign">
          <label className="mass-messaging-label" htmlFor="campaignName">
            Nombre de la campaña:
          </label>
          <input
            id="campaignName"
            type="text"
            className="mass-messaging-input"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
          />
        </div>
        <div className="mass-messaging-created-by">
          Creada por: <strong>{usr_name || '—'}</strong>
        </div>
      </div>

      {/* Destinatarios - Tabs */}
      <div className="mass-messaging-recipients">
        <label className="mass-messaging-label mass-messaging-label--section">
          Seleccionar destinatarios:
        </label>

        <Nav
          variant="tabs"
          activeKey={recipientTab}
          onSelect={(key) => setRecipientTab(key)}
          className="mass-messaging-tabs"
        >
          <Nav.Item>
            <Nav.Link eventKey="proyecto">Por Proyecto</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="pais">Por País</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="modelo">Por Modelo</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="serial">Por Serial</Nav.Link>
          </Nav.Item>
        </Nav>

        <div className="mass-messaging-tab-content">
          {recipientTab === 'proyecto' && (
            <div className="mass-messaging-tab-pane">
              <label className="mass-messaging-label">
                Que pertenezcan al proyecto:
              </label>
              <div className="mass-messaging-select-wrapper">
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={projects}
                  onChange={(option) => setSelectedProject(option)}
                  placeholder="Buscar proyecto..."
                  isSearchable
                  isClearable
                  value={selectedProject}
                />
              </div>
            </div>
          )}

          {recipientTab === 'pais' && (
            <div className="mass-messaging-tab-pane">
              <label className="mass-messaging-label">
                Que pertenezcan al país:
              </label>
              <div className="mass-messaging-select-wrapper">
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={countries}
                  onChange={(option) => setSelectedCountry(option)}
                  placeholder="Buscar país..."
                  isSearchable
                  isClearable
                  value={selectedCountry}
                />
              </div>
            </div>
          )}

          {recipientTab === 'modelo' && (
            <div className="mass-messaging-tab-pane">
              <label className="mass-messaging-label">
                Que sean del modelo:
              </label>
              <div className="mass-messaging-select-wrapper">
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={models}
                  onChange={(option) => setSelectedModel(option)}
                  placeholder="Buscar modelo..."
                  isSearchable
                  isClearable
                  value={selectedModel}
                />
              </div>
            </div>
          )}

          {recipientTab === 'serial' && (
            <div className="mass-messaging-serial-pane">
              <label className="mass-messaging-label" htmlFor="serialInput">
                Ingrese el serial (separar con espacio o coma):
              </label>
              <div className="mass-messaging-serial-wrapper">
                <div className="mass-messaging-serial-chips">
                  {serialChips.map((serial, index) => (
                    <span key={`${serial}-${index}`} className="mass-messaging-chip">
                      <span className="mass-messaging-chip-text">{serial}</span>
                      <button
                        type="button"
                        className="mass-messaging-chip-remove"
                        onClick={() => removeSerialChip(index)}
                        aria-label={`Quitar ${serial}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    ref={serialInputRef}
                    id="serialInput"
                    type="text"
                    className="mass-messaging-serial-input"
                    placeholder={serialChips.length === 0 ? 'Ej: T1TB000010' : ''}
                    value={serialInputValue}
                    onChange={handleSerialInputChange}
                    onKeyDown={handleSerialInputKeyDown}
                  />
                </div>
                <button
                  type="button"
                  className="mass-messaging-btn mass-messaging-btn--clear"
                  onClick={clearAllSerials}
                  disabled={serialChips.length === 0 && !serialInputValue}
                >
                  Limpiar
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mass-messaging-recipients-actions">
          <button
            type="button"
            className="mass-messaging-btn mass-messaging-btn--recipients"
            onClick={handleOpenRecipientsModal}
          >
            Ver total de destinatarios
          </button>
        </div>
      </div>

      {/* Mensaje */}
      <div className="mass-messaging-row">
        <div className="mass-messaging-field mass-messaging-field--full">
          <label className="mass-messaging-label" htmlFor="messageArea">
            Mensaje <span className="text-muted">({message.length}/{MESSAGE_MAX_LENGTH})</span>
          </label>
          <textarea
            id="messageArea"
            className="mass-messaging-textarea"
            rows={8}
            maxLength={MESSAGE_MAX_LENGTH}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>
      </div>

      {/* Call2Action: solo links cortos creados en QRshorter */}
      <div className="mass-messaging-row">
        <div className="mass-messaging-field mass-messaging-field--full">
          <label className="mass-messaging-label" htmlFor="callToAction">
            Call2Action (link corto):
          </label>
          <div className="mass-messaging-select-wrapper">
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              inputId="callToAction"
              options={shortLinksOptions}
              value={(() => {
                if (!callToAction) return null;
                const byValue = shortLinksOptions.find((o) => o.value === callToAction);
                if (byValue) return byValue;
                const slug = shortSlugFromStoredCallAction(callToAction);
                if (slug) {
                  const bySlug = shortLinksOptions.find((o) => o.shortSlug === slug);
                  if (bySlug) return bySlug;
                }
                return { value: callToAction, label: callToAction };
              })()}
              onChange={(option) => setCallToAction(option ? option.value : '')}
              placeholder={shortLinksLoading ? 'Cargando enlaces...' : 'Buscar enlace corto...'}
              isClearable
              isSearchable
              isLoading={shortLinksLoading}
            />
          </div>
        </div>
      </div>

      {/* Botones */}
      <div className="mass-messaging-actions">
        <button
          type="button"
          className="mass-messaging-btn mass-messaging-btn--draft"
          onClick={handleSave}
          disabled={saving || dispatchLoading || campaignStatus === 1}
        >
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
        <button
          type="button"
          className="mass-messaging-btn mass-messaging-btn--send"
          onClick={handleEnviar}
          disabled={saving || dispatchLoading || campaignStatus === 1}
        >
          {dispatchLoading ? 'Enviando...' : 'Enviar'}
        </button>
        <button
          type="button"
          className="mass-messaging-btn mass-messaging-btn--schedule"
          onClick={handleProgramarClick}
          disabled={saving || dispatchLoading || campaignStatus === 1}
        >
          Programar
        </button>
      </div>

      {/* Modal programar envío */}
      <Modal_proyectos
        title="Programar envío"
        isOpen={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
      >
        <div className="mass-messaging-modal-body">
          <p className="mb-3">Seleccione la fecha y hora en que debe entregarse la notificación a los dispositivos.</p>
          <label className="form-label">Fecha y hora de entrega</label>
          <input
            type="datetime-local"
            className="form-control"
            value={scheduleDateTime}
            onChange={(e) => setScheduleDateTime(e.target.value)}
          />
        </div>
        <div className="modal-footer border-top pt-3 mt-3">
          <button type="button" className="btn btn-secondary" onClick={() => setScheduleModalOpen(false)}>
            Cancelar
          </button>
          <button type="button" className="btn btn-primary" onClick={handleProgramarConfirm}>
            Confirmar
          </button>
        </div>
      </Modal_proyectos>

      {/* Modal confirmar envío */}
      <Modal_proyectos
        title={confirmSendMode === 'immediate' ? 'Confirmar envío' : 'Confirmar programación'}
        isOpen={confirmSendModalOpen}
        onClose={() => {
          if (!dispatchLoading) {
            setConfirmSendModalOpen(false);
            setPendingDispatchId(null);
            setPendingDeliveryDate(null);
            setConfirmScheduledDate('');
          }
        }}
      >
        <div className="mass-messaging-modal-body">
          {confirmSendMode === 'immediate' ? (
            <p className="mb-0">
              ¿Enviar la campaña <strong>{campaignName || '—'}</strong> ahora? Las notificaciones se entregarán de inmediato.
            </p>
          ) : (
            <p className="mb-0">
              ¿Programar el envío de la campaña <strong>{campaignName || '—'}</strong> para el <strong>{confirmScheduledDate}</strong>?
            </p>
          )}
        </div>
        <div className="modal-footer border-top pt-3 mt-3">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setConfirmSendModalOpen(false);
              setPendingDispatchId(null);
              setPendingDeliveryDate(null);
              setConfirmScheduledDate('');
            }}
            disabled={dispatchLoading}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleConfirmSendModalConfirm}
            disabled={dispatchLoading}
          >
            {dispatchLoading ? 'Enviando...' : confirmSendMode === 'immediate' ? 'Enviar' : 'Programar'}
          </button>
        </div>
      </Modal_proyectos>
      </fieldset>
      )}

      {/* Modal destinatarios */}
      <Modal_proyectos
        title="Destinatarios de la campaña"
        isOpen={recipientsModalOpen}
        onClose={() => {
          setRecipientsModalOpen(false);
          setRecipientsError(null);
        }}
      >
        <div className="mass-messaging-modal-body">
          {recipientsLoading && (
            <div className="mass-messaging-modal-loading">
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              Calculando total de destinatarios...
            </div>
          )}
          {!recipientsLoading && recipientsError && (
            <div className="alert alert-warning mb-0">
              {recipientsError}
            </div>
          )}
          {!recipientsLoading && !recipientsError && recipientsData && (
            <>
              <p className="mass-messaging-modal-summary">
                Se enviará el mensaje a <strong>{recipientsData.total_serials}</strong> dispositivo(s).
                De ellos, <strong>{recipientsData.total_with_version_gt_214}</strong> lo recibirán de inmediato (versión &gt; 214).
                El resto lo recibirá cuando actualice el software.
              </p>
              <div className="mass-messaging-modal-chart">
                {recipientsPieData && recipientsData.total_serials > 0 && (
                  <div style={{ position: 'relative', height: '280px', width: '100%' }}>
                    <Pie
                      data={recipientsPieData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { position: 'bottom' },
                          datalabels: {
                            color: '#333',
                            font: { weight: 'bold', size: 12 },
                            formatter: (value, ctx) => {
                              const total = ctx.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
                              if (total === 0) return '';
                              return value + '\n(' + ((value / total) * 100).toFixed(0) + '%)';
                            },
                          },
                        },
                      }}
                    />
                  </div>
                )}
                {recipientsData.total_serials === 0 && (
                  <p className="text-muted mb-0">No hay destinatarios con la selección actual.</p>
                )}
              </div>
            </>
          )}
        </div>
        <div className="modal-footer border-top pt-3 mt-3">
          <button type="button" className="btn btn-secondary" onClick={() => setRecipientsModalOpen(false)}>
            Cerrar
          </button>
        </div>
      </Modal_proyectos>
    </div>
  );
}

export default MassMessaging;
